import React, { useEffect, useState } from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import authService from './auth-service';
import { logError } from '../utils';

interface AuthInitializerProps {
  children: React.ReactNode;
}

let isInitializing = false;

/**
 * Component that initializes the authentication service before rendering children
 */
export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    const initAuth = async () => {
      try {
        isInitializing = true;

        const success = await authService.init();
        if (success) {
          setInitialized(true);
        } else {
          setError('Failed to initialize authentication service');
        }
      } catch (err) {
        logError('Error initializing auth service:', err);
        setError('An error occurred while initializing authentication');
      } finally {
        isInitializing = false;
      }
    };

    if (!initialized && !error) {
      initAuth();
    }
  }, [initialized, error]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="h5" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body1">{error}</Typography>
      </Box>
    );
  }

  if (!initialized) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: 20 }}>
          Initializing application...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
